<template>
  <AppEventContainer>
    <EventHeader class="w-full md:w-2/3" :poker-run-event="currentPokerRunEvent" :show-type="true"></EventHeader>

    <div v-for="transaction in completedTransactions"
         :key="transaction.transactionDetails.transactionId"
         class="mb-4">


      <!-- Transaction Details -->
    <div class="flex gap-2 justify-between">
      <EventSummaryItem label="Transaction time">
        <p v-if="transaction?.transactionTimestamp">
          {{ formatFullDate(transaction.transactionTimestamp.toDate()) }}
        </p>
        <p v-if="transaction?.transactionTimestamp">
          {{ formatTime(transaction.transactionTimestamp.toDate()) }}
        </p>
      </EventSummaryItem>

      <EventSummaryItem v-if="transaction?.transactionDetails.last4" label="Payment method">
        Card ending in {{ transaction?.transactionDetails.last4 }}
      </EventSummaryItem>
    </div>

    <!-- Confirmation Code -->
    <div class="mb-8">
      <EventSummaryItem label="Confirmation code">
        <div class="">
          <p class="font-medium">{{ transaction?.transactionDetails.transactionId }}</p>
          <button @click="handleCopy(transaction?.transactionDetails.transactionId)"
            class="text-action hover:text-action-hover flex items-center">
            Copy code
            <i class="text-action fa-copy far ml-1"></i>
          </button>
        </div>
      </EventSummaryItem>
    </div>

      <EventCost
        class="w-full md:w-2/3"
        :line-items="getAggregateLineItems(getLineItemsWithServiceFee(transaction))"
        :total-amount="transaction.transactionDetails.amount"
        :show-change-button="false">
      </EventCost>

      <div class="h-px bg-gray-200 w-full my-4" v-if="completedTransactions.indexOf(transaction) !== completedTransactions.length - 1"></div>
    </div>

    <div v-if="completedTransactions.length === 0" class="text-center my-4">
      <p class="text-lg">No completed transactions found for this event.</p>
    </div>
  </AppEventContainer>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { storeToRefs } from "pinia";
import EventHeader from "@/components/EventHeader.vue";
import EventCost from "@/components/EventCost.vue";
import EventSummaryItem from "@/components/EventSummaryItem.vue";
import { useEventParticipantStore } from "@/store/EventParticipant";
import UserTransactionDataService from "@/services/UserTransactionDataService";
import { useUserProfileStore } from "@/store/UserProfile";
import { format } from "date-fns";
import { UserTransaction } from "@models/UserTransaction";
import { Item } from "@models/Item";
import useDate from "@/modules/useDate";
import Constants from "@/models/Constants";

const { formatTime, formatFullDate } = useDate();

const { profile } = useUserProfileStore();
const { currentPokerRunEvent } = storeToRefs(useEventParticipantStore());
const completedTransactions = ref<UserTransaction[]>([]);

const getLineItemsWithServiceFee = (transaction: UserTransaction): Item[] => {
  const fees = transaction.transactionDetails.lineItems;
  const sumOfFees = fees.reduce((prev, curr) => prev + curr.amount, 0);

  return [
    ...transaction.transactionDetails.lineItems,
    {
      name: Constants.SERVICE_FEE,
      amount: transaction.transactionDetails.amount - sumOfFees,
      itemType: Constants.SERVICE_FEE,
    },
  ];
};

const getAggregateLineItems = (lineItems: Item[]): Item[] =>
  lineItems.reduce((prev, curr) => {
    const item = prev.find((x) => x.itemId === curr.itemId);
    if (item) {
      item.amount += curr.amount;
      item.quantity = (item.quantity || 0) + 1;
    } else {
      const updateItem = { ...curr, quantity: 1 };
      prev.push(updateItem);
    }
    return prev;
  }, [] as Item[]);

const initialize = async () => {
  const transactions = await UserTransactionDataService.getUserTransactions(
    profile.id,
    currentPokerRunEvent.value.id as string
  );

  completedTransactions.value = transactions
    .filter(x => x.status === "complete")
    .sort((a, b) =>
      b.transactionTimestamp.toDate().getTime() - a.transactionTimestamp.toDate().getTime()
    );
};

initialize();
</script>
